import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Per-branch freight options (`im_freight_options`, GEN-3281): the carriers
 * and freight terms a buyer can pick on a branch's purchase orders, with the
 * carrier account and the weight and order-value limits that say when each
 * applies. Options are learned from past releases (suggested until confirmed)
 * or added by hand. `branch_default` only marks options earlier refreshes made
 * from branches' customer records; none are made any more.
 */

export const FREIGHT_OPTION_SOURCES = [
  "branch_default",
  "observed_release",
  "manual",
] as const;
export const FREIGHT_OPTION_STATUSES = [
  "suggested",
  "confirmed",
  "archived",
] as const;
export const FREIGHT_TERMS_KINDS = [
  "prepaid",
  "prepaid_and_charge",
  "collect",
  "other",
] as const;

export const FreightOptionSourceSchema = z.enum(FREIGHT_OPTION_SOURCES);
export const FreightOptionStatusSchema = z.enum(FREIGHT_OPTION_STATUSES);
export const FreightTermsKindSchema = z.enum(FREIGHT_TERMS_KINDS);

/** A decimal amount as a string, e.g. "1500.00". */
const MoneyStringSchema = z
  .string()
  .regex(/^\d{1,13}(\.\d{1,2})?$/, "Enter an amount like 1500 or 1500.00");
const WeightSchema = z.number().nonnegative().max(1_000_000);
/** A printed PO instruction line; the ERP prints 40 characters per line. */
const InstructionLineSchema = z.string().trim().min(1).max(40);

export const FreightOptionRowSchema = z.object({
  id: z.string().uuid(),
  warehouseId: z.string().uuid(),
  warehouseCode: z.string().nullable(),
  warehouseName: z.string().nullable(),
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable(),
  shipViaCode: z.string(),
  shipViaDescription: z.string().nullable(),
  freightTermsCode: z.string().nullable(),
  freightTermsDescription: z.string().nullable(),
  freightTermsKind: FreightTermsKindSchema.nullable(),
  carrierAccount: z.string().nullable(),
  /** The default shipping instructions, preselected when the option is picked. */
  instructions: z.array(z.string()),
  /** Shipping instructions saved for the option and not archived, its default included. */
  savedInstructionCount: z.number().int(),
  minWeightLbs: z.number().nullable(),
  maxWeightLbs: z.number().nullable(),
  minOrderValue: z.string().nullable(),
  maxOrderValue: z.string().nullable(),
  isDefault: z.boolean(),
  source: FreightOptionSourceSchema,
  status: FreightOptionStatusSchema,
  observedCount: z.number().int(),
  lastObservedOn: z.string().nullable(),
  note: z.string().nullable(),
  updatedAt: z.string(),
});
export type FreightOptionRow = z.infer<typeof FreightOptionRowSchema>;

export const ListFreightOptionsInputSchema = z.object({
  /** Limit to one branch; omit for every branch. */
  warehouseId: z.string().uuid().optional(),
  /** Include archived options. Defaults to false. */
  includeArchived: z.boolean().optional(),
});
export const ListFreightOptionsResponseSchema = z.object({
  options: z.array(FreightOptionRowSchema),
});

export const FreightCodeRowSchema = z.object({
  code: z.string(),
  description: z.string().nullable(),
  termsKind: FreightTermsKindSchema.nullable(),
});
export const ListFreightCodesInputSchema = z.object({
  warehouseId: z.string().uuid(),
});
export const ListFreightCodesResponseSchema = z.object({
  shipVia: z.array(FreightCodeRowSchema),
  freightTerms: z.array(FreightCodeRowSchema),
  creditTerms: z.array(FreightCodeRowSchema),
});
export type ListFreightCodesResponse = z.infer<
  typeof ListFreightCodesResponseSchema
>;

/** The fields a person can set on an option, shared by create and update. */
const editableShape = {
  supplierId: z.string().uuid().nullable().optional(),
  shipViaCode: z.string().trim().min(1).max(16),
  freightTermsCode: z.string().trim().min(1).max(16).nullable().optional(),
  carrierAccount: z.string().trim().min(1).max(64).nullable().optional(),
  instructions: z.array(InstructionLineSchema).max(10).optional(),
  minWeightLbs: WeightSchema.nullable().optional(),
  maxWeightLbs: WeightSchema.nullable().optional(),
  minOrderValue: MoneyStringSchema.nullable().optional(),
  maxOrderValue: MoneyStringSchema.nullable().optional(),
  isDefault: z.boolean().optional(),
  note: z.string().max(1000).nullable().optional(),
};

const weightsInOrder = (v: {
  minWeightLbs?: number | null;
  maxWeightLbs?: number | null;
}): boolean =>
  v.minWeightLbs == null ||
  v.maxWeightLbs == null ||
  v.minWeightLbs <= v.maxWeightLbs;
const orderValuesInOrder = (v: {
  minOrderValue?: string | null;
  maxOrderValue?: string | null;
}): boolean =>
  v.minOrderValue == null ||
  v.maxOrderValue == null ||
  Number(v.minOrderValue) <= Number(v.maxOrderValue);
const RANGE_MESSAGE = "A minimum cannot be above its maximum.";
const WEIGHT_RANGE = { message: RANGE_MESSAGE, path: ["minWeightLbs"] };
const ORDER_VALUE_RANGE = { message: RANGE_MESSAGE, path: ["minOrderValue"] };

export const CreateFreightOptionInputSchema = z
  .object({ warehouseId: z.string().uuid(), ...editableShape })
  .refine(weightsInOrder, WEIGHT_RANGE)
  .refine(orderValuesInOrder, ORDER_VALUE_RANGE);
export type CreateFreightOptionInput = z.infer<
  typeof CreateFreightOptionInputSchema
>;

export const UpdateFreightOptionInputSchema = z
  .object({
    id: z.string().uuid(),
    ...editableShape,
    shipViaCode: editableShape.shipViaCode.optional(),
    status: FreightOptionStatusSchema.optional(),
  })
  .refine(weightsInOrder, WEIGHT_RANGE)
  .refine(orderValuesInOrder, ORDER_VALUE_RANGE);
export type UpdateFreightOptionInput = z.infer<
  typeof UpdateFreightOptionInputSchema
>;

export const FreightOptionMutationResponseSchema = z.object({
  option: FreightOptionRowSchema,
});

export const RefreshFreightOptionsInputSchema = z.object({
  /** How far back releases are read. Defaults to 365 days. */
  lookbackDays: z.number().int().min(1).max(1095).optional(),
});
/** Options learned from past releases: new ones and ones seen again. */
export const RefreshFreightOptionsResponseSchema = z.object({
  inserted: z.number(),
  updated: z.number(),
});

export const DraftFreightChoiceSchema = z.enum(["suggested", "override"]);

/** The freight a draft purchase order goes out with. */
export const FreightLoadStatusSchema = z.enum([
  "fits",
  "over_limit",
  "under_limit",
  "unknown_weight",
  "no_limit",
]);
export type FreightLoadStatus = z.infer<typeof FreightLoadStatusSchema>;

export const FreightLoadCheckSchema = z.object({
  status: FreightLoadStatusSchema,
  /** The limit the order breaks, when over or under one. */
  limitLbs: z.number().nullable(),
});
export type FreightLoadCheck = z.infer<typeof FreightLoadCheckSchema>;

export const DraftPurchaseOrderFreightSchema = z.object({
  draftPurchaseOrderId: z.string().uuid(),
  warehouseId: z.string().uuid(),
  supplierId: z.string().uuid(),
  /** False once the PO was sent to the ERP. */
  editable: z.boolean(),
  shipViaCode: z.string().nullable(),
  freightTermsCode: z.string().nullable(),
  carrierAccount: z.string().nullable(),
  instructions: z.array(z.string()),
  freightOptionId: z.string().uuid().nullable(),
  freightChoice: DraftFreightChoiceSchema.nullable(),
  /** The option the branch's rules suggest for this order now, if any. */
  suggestedOptionId: z.string().uuid().nullable(),
  /** Pounds over the lines whose item weight is known. */
  orderWeightLbs: z.number(),
  weighedLines: z.number().int(),
  totalLines: z.number().int(),
  /** Whether the order's weight fits the chosen freight's weight limits. */
  loadCheck: FreightLoadCheckSchema,
});
export type DraftPurchaseOrderFreight = z.infer<
  typeof DraftPurchaseOrderFreightSchema
>;

export const GetDraftFreightInputSchema = z.object({
  draftPurchaseOrderId: z.string().uuid(),
});

/** A carrier account typed over the option's. */
const CarrierAccountInputSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .nullable()
  .optional();
/** Printed lines typed over the option's; [] prints none. */
const InstructionLinesInputSchema = z
  .array(InstructionLineSchema)
  .max(10)
  .optional();

export const SetDraftFreightInputSchema = z.object({
  draftPurchaseOrderId: z.string().uuid(),
  /** Start from one of the branch's confirmed options. */
  freightOptionId: z.string().uuid().nullable().optional(),
  /** Typed fields win over the option's. */
  shipViaCode: z.string().trim().min(1).max(16).optional(),
  freightTermsCode: z.string().trim().min(1).max(16).nullable().optional(),
  carrierAccount: CarrierAccountInputSchema,
  instructions: InstructionLinesInputSchema,
});
export type SetDraftFreightInput = z.infer<typeof SetDraftFreightInputSchema>;

/**
 * The freight a buyer picked while releasing a purchase order: one of the
 * branch's confirmed options, and the carrier account only when they typed a
 * different one.
 */
export const ReleaseFreightChoiceSchema = z.object({
  freightOptionId: z.string().uuid(),
  carrierAccount: CarrierAccountInputSchema,
  /** Shipping instructions other than the option's default, or none ([]). */
  instructions: InstructionLinesInputSchema,
});
export type ReleaseFreightChoice = z.infer<typeof ReleaseFreightChoiceSchema>;

export const SuggestOrderFreightInputSchema = z.object({
  supplierId: z.string().uuid(),
  warehouseId: z.string().uuid(),
  /** The value of the lines being released or ordered. */
  orderValue: z.number().nonnegative(),
  lines: z
    .array(
      z.object({
        productV3Key: z.string().max(255).nullable(),
        quantity: z.number().nonnegative(),
      }),
    )
    .max(2000),
  /** Lines added to this open draft: its own lines and total count too. */
  draftPurchaseOrderId: z.string().uuid().optional(),
});
export type SuggestOrderFreightInput = z.infer<
  typeof SuggestOrderFreightInputSchema
>;

/** The freight the branch's rules suggest for an order not yet saved. */
export const OrderFreightSuggestionSchema = z.object({
  suggestedOptionId: z.string().uuid().nullable(),
  /** Pounds over the lines whose item weight is known. */
  orderWeightLbs: z.number(),
  weighedLines: z.number().int(),
  totalLines: z.number().int(),
});
export type OrderFreightSuggestion = z.infer<
  typeof OrderFreightSuggestionSchema
>;

export const FREIGHT_OPTION_INSTRUCTION_SOURCES = [
  "learned",
  "manual",
] as const;
export const FREIGHT_OPTION_INSTRUCTION_STATUSES = [
  "active",
  "archived",
] as const;

/** A shipping instruction saved for a freight option. */
export const FreightOptionInstructionRowSchema = z.object({
  id: z.string().uuid(),
  lines: z.array(z.string()),
  source: z.enum(FREIGHT_OPTION_INSTRUCTION_SOURCES),
  status: z.enum(FREIGHT_OPTION_INSTRUCTION_STATUSES),
  observedCount: z.number().int(),
  lastObservedOn: z.string().nullable(),
  /** The option's default: preselected when the option is picked. */
  isDefault: z.boolean(),
});
export type FreightOptionInstructionRow = z.infer<
  typeof FreightOptionInstructionRowSchema
>;

export const ListFreightOptionInstructionsInputSchema = z.object({
  freightOptionId: z.string().uuid(),
  /** Include archived instructions. Defaults to false. */
  includeArchived: z.boolean().optional(),
});
export const ListFreightOptionInstructionsResponseSchema = z.object({
  /** Most used first, then most recent. */
  instructions: z.array(FreightOptionInstructionRowSchema),
});

export const AddFreightOptionInstructionInputSchema = z.object({
  freightOptionId: z.string().uuid(),
  lines: z.array(InstructionLineSchema).min(1).max(10),
  makeDefault: z.boolean().optional(),
});

export const UpdateFreightOptionInstructionInputSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(FREIGHT_OPTION_INSTRUCTION_STATUSES).optional(),
  /** Make it the option's default shipping instructions. */
  makeDefault: z.literal(true).optional(),
});
export type UpdateFreightOptionInstructionInput = z.infer<
  typeof UpdateFreightOptionInstructionInputSchema
>;

export const FreightOptionInstructionMutationResponseSchema = z.object({
  instruction: FreightOptionInstructionRowSchema,
});

export const freightOptionsContracts = {
  suggestOrderFreight: oc
    .input(SuggestOrderFreightInputSchema)
    .output(OrderFreightSuggestionSchema)
    .route({
      method: "POST",
      summary: "Suggest freight for an order being released",
      description:
        "Returns the confirmed branch freight option the rules would pick for an order not yet saved (or lines being added to an open draft), from its vendor, value and item weights.",
    }),
  getDraftFreight: oc
    .input(GetDraftFreightInputSchema)
    .output(DraftPurchaseOrderFreightSchema)
    .route({
      method: "POST",
      summary: "Get a draft purchase order's freight",
      description:
        "Returns the carrier, freight terms, carrier account and instructions a draft purchase order goes out with, and the option the branch's rules suggest for it.",
    }),
  setDraftFreight: oc
    .input(SetDraftFreightInputSchema)
    .output(DraftPurchaseOrderFreightSchema)
    .route({
      method: "POST",
      summary: "Set a draft purchase order's freight",
      description:
        "Sets the freight of a draft purchase order not yet sent to the ERP, from one of the branch's confirmed options and/or typed fields.",
    }),
  list: oc
    .input(ListFreightOptionsInputSchema)
    .output(ListFreightOptionsResponseSchema)
    .route({
      method: "POST",
      summary: "List branch freight options",
      description:
        "Lists the carriers and freight terms buyers can pick on purchase orders, per branch, with their source, status and limits.",
    }),
  listCodes: oc
    .input(ListFreightCodesInputSchema)
    .output(ListFreightCodesResponseSchema)
    .route({
      method: "POST",
      summary: "List a branch's freight codes",
      description:
        "Lists the carrier (ship-via), freight-terms and credit-terms codes of the branch's division, with descriptions and who pays the freight.",
    }),
  create: oc
    .input(CreateFreightOptionInputSchema)
    .output(FreightOptionMutationResponseSchema)
    .route({
      method: "POST",
      summary: "Add a branch freight option",
      description:
        "Adds a confirmed freight option for a branch, optionally for one vendor.",
    }),
  update: oc
    .input(UpdateFreightOptionInputSchema)
    .output(FreightOptionMutationResponseSchema)
    .route({
      method: "POST",
      summary: "Update a branch freight option",
      description:
        "Edits, confirms or archives a freight option. Making one the default clears the default from the branch's other options for the same vendor scope.",
    }),
  listInstructions: oc
    .input(ListFreightOptionInstructionsInputSchema)
    .output(ListFreightOptionInstructionsResponseSchema)
    .route({
      method: "POST",
      summary: "List a freight option's shipping instructions",
      description:
        "Lists the shipping instructions saved for a freight option, learned from past releases or added by hand, most used first, with the option's default marked.",
    }),
  addInstruction: oc
    .input(AddFreightOptionInstructionInputSchema)
    .output(FreightOptionInstructionMutationResponseSchema)
    .route({
      method: "POST",
      summary: "Save a shipping instruction for a freight option",
      description:
        "Saves shipping instruction lines for a freight option, or brings back the same text if it was archived, and can make it the option's default.",
    }),
  updateInstruction: oc
    .input(UpdateFreightOptionInstructionInputSchema)
    .output(FreightOptionInstructionMutationResponseSchema)
    .route({
      method: "POST",
      summary: "Archive, restore or make default a shipping instruction",
      description:
        "Archives or restores a freight option's saved shipping instruction, or makes it the option's default. Archiving the default leaves the option without default instructions.",
    }),
  refresh: oc
    .input(RefreshFreightOptionsInputSchema)
    .output(RefreshFreightOptionsResponseSchema)
    .route({
      method: "POST",
      summary: "Refresh freight option suggestions",
      description:
        "Suggests options learned from recent purchase-order releases, without undoing anyone's edits. Nothing is confirmed or made a default.",
    }),
};
